import React, { useState, useEffect } from "react";
import {
  VStack,
  Box,
  SimpleGrid,
  Text,
  Button,
  useToast,
  Spinner,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import { FaSignInAlt, FaGlobe } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faLockOpen } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../ThemeContext/ThemeContext";
import {
  getUserProfile,
  getUserProgress,
  setUserProgress,
} from "../../utils/userUtils";
import { getActivity, getActivityWorld } from "../../utils/activityUtils";
import activityList from "../../Assets/activities/activityList.json";

function Activity() {
  const { currentTheme } = useTheme();
  const bgColor = currentTheme.color1;
  const toast = useToast();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAcitivtyUnlockModalOpen, setIsActivityUnlockModalOpen] =
    useState(false);
  const [activityWorldName, setActivityWorldName] = useState("");
  const [activityName, setActivityName] = useState("");

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const progressData = getUserProgress();
        const activityMap = new Map(
          activityList.map((item) => [item.activity, item.name])
        );
        const loadedActivities = progressData.activityProgresses.map(
          (activity) => ({
            id: activity.name,
            name: activityMap.get(activity.name),
            unlocked: activity.unlocked,
            path: `/activity/${activity.name.toLowerCase().replace(/\s/g, "")}`,
          })
        );

        setActivities(loadedActivities);
      } catch (error) {
        console.error("Error fetching activities:", error);
        toast({
          title: "Error",
          description: "Failed to load activities.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false); // Set loading to false regardless of the outcome
      }
    };

    fetchActivities();
  }, [toast]);

  const redoActivity = async (activityName, path) => {
    try {
      const activityData = getActivity(activityName);
      navigate(path, { state: { activityData }, from: "activity" });
    } catch (error) {
      console.error("Error fetching activity details:", error);
      toast({
        title: "Error",
        description: `Failed to load details for ${activityName}.`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleUnlockActivity = () => {
    const progressData = getUserProgress();
    const activityIndex = progressData.activityProgresses.findIndex(
      (activity) =>
        activity.name ===
        activityList.find((a) => a.name == activityName)?.activity
    );

    if (activityIndex !== -1) {
      progressData.activityProgresses[activityIndex].unlocked = true;
      setUserProgress(progressData);
      toast({
        title: "Activity Unlocked",
        description: `${activityName} has been unlocked!`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setIsActivityUnlockModalOpen(false);
      activities[activityIndex].unlocked = true;
      setActivities([...activities]);
    } else {
      toast({
        title: "Error",
        description: "Failed to unlock the activity.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (isLoading) {
    return (
      <Center height="50vh">
        <Spinner size="xl" label="Loading activities..." />
      </Center>
    );
  }

  return (
    <Box p={50}>
      <SimpleGrid columns={{ sm: 2, md: 3, lg: 4 }} spacing={5}>
        {activities.map((activity) => (
          <Box
            key={activity.id}
            p={5}
            shadow="md"
            borderWidth="1px"
            textAlign="center"
            bg={activity.unlocked ? currentTheme.color6 : "gray.200"}
            borderRadius="md"
            sx={{
              filter: activity.unlocked ? "none" : "grayscale(100%)",
            }}
          >
            <FontAwesomeIcon
              icon={activity.unlocked ? faLockOpen : faLock}
              size="3x"
              color={activity.unlocked ? "green" : "gray"}
            />
            <Text mt={2}>{activity.name}</Text>
            <Button
              mt={4}
              color="white"
              bg={activity.unlocked ? currentTheme.color4 : "gray.600"}
              _hover={{
                bg: activity.unlocked ? currentTheme.color5 : "gray.600",
                transform: activity.unlocked ? "scale(1.05)" : "none",
              }}
              borderRadius="lg"
              onClick={
                activity.unlocked
                  ? () => redoActivity(activity.id, activity.path)
                  : () => {
                      setActivityName(activity.name);
                      setActivityWorldName(getActivityWorld(activity.name));
                      setIsActivityUnlockModalOpen(true);
                    }
              }
            >
              {activity.unlocked ? "Redo Activity" : "Unlock Activity"}
            </Button>
          </Box>
        ))}
      </SimpleGrid>
      {/* Modal for unlocking activity */}
      <Modal
        isOpen={isAcitivtyUnlockModalOpen}
        onClose={() => setIsActivityUnlockModalOpen(false)}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Unlocking Activity</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to unlock this activity? For a better
            experience, it is recommended to unlock the activity by completing
            the "{activityWorldName}" world.
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="yellow" mr={3} onClick={handleUnlockActivity}>
              Unlock
            </Button>
            <Button
              variant="ghost"
              onClick={() => setIsActivityUnlockModalOpen(false)}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Activity;
